import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Building2,
  Users2,
  ListChecks,
  LogOut,
  ChevronLeft,
  ChevronRight,
  MessageSquarePlus,
  Send,
  Loader2,
  MessageSquare,
  ClipboardList,
  LayoutDashboard,
  Briefcase,
  FileText,
  CreditCard,
  UsersRound,
  Cog,
  HelpCircle,
  Menu,
  X,
  TrendingUp,
  CalendarDays,
  Lock,
  Package,
  Lightbulb,
  Target,
  CheckSquare,
  FlaskConical,
} from 'lucide-react';
import { DollarSign } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { feedbackApi } from '../../lib/api';

const Sidebar = ({ onNavigate }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('sugestao');
  const [sendingFeedback, setSendingFeedback] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) {
      toast.error('Escreva sua sugestão');
      return;
    }
    setSendingFeedback(true);
    try {
      await feedbackApi.send(feedbackMessage, feedbackType);
      toast.success('Enviado!');
      setShowFeedback(false);
      setFeedbackMessage('');
    } catch (error) {
      toast.error('Erro ao enviar');
    } finally {
      setSendingFeedback(false);
    }
  };

  const handleNavClick = () => {
    setMobileOpen(false);
    onNavigate && onNavigate();
  };

  const superAdminItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin-secret' },
    { icon: Building2, label: 'Empresas', path: '/admin-secret/companies' },
    { icon: Lightbulb, label: 'Banco de Ideias', path: '/banco-ideias' },
  ];

  const menuGroups = [
    {
      title: 'Menu',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', permission: 'daily_control' },
        { icon: Target, label: 'Ciclo de Melhoria', path: '/ciclo-melhoria', permission: 'daily_control' },
        { icon: Lightbulb, label: 'Banco de Ideias', path: '/banco-ideias', permission: 'daily_control' },
        { icon: Briefcase, label: 'Projetos', path: '/projects', permission: 'daily_control' },
        { icon: CheckSquare, label: 'Tarefas', path: '/tasks', permission: 'daily_control' },
      ]
    },
    {
      title: 'Operacional',
      items: [
        { icon: UsersRound, label: 'Humanas', path: '/humanas', permission: 'daily_control' },
        { icon: ClipboardList, label: 'Escala do Dia', path: '/daily-control', permission: 'daily_control' },
        { icon: Briefcase, label: 'Relação de OSs', path: '/os-gestao', permission: 'daily_control' },
        { icon: Package, label: 'Estoque', path: '/estoque', permission: 'daily_control' },
        { icon: UsersRound, label: 'Por Pessoa', path: '/relatorio-pessoa', permission: 'reports', adminOnly: true },
      ]
    },
    {
      title: 'Comercial',
      items: [
        { icon: DollarSign, label: 'Orçamentos', path: '/orcamentos', permission: 'all' },
      ]
    },
    {
      title: 'Config',
      items: [
        { icon: Users2, label: 'Equipe', path: '/users', permission: 'users', adminOnly: true },
        { icon: CalendarDays, label: 'Feriados', path: '/feriados', permission: 'admin_only', adminOnly: true },
        { icon: Lock, label: 'Competências', path: '/competencias', permission: 'admin_only', adminOnly: true },
        { icon: Cog, label: 'Configurações', path: '/settings', permission: 'settings', adminOnly: true },
        { icon: HelpCircle, label: 'Ajuda', path: '/help', permission: 'daily_control' },
      ]
    },
    {
      title: 'Em Desenv.',
      adminOnly: true,
      items: [
        { icon: TrendingUp, label: 'Performance', path: '/performance', permission: 'reports', adminOnly: true },
        { icon: FileText, label: 'Ponto CLT com Obs.', path: '/folha-clt', permission: 'reports', adminOnly: true },
        { icon: CreditCard, label: 'Folha CLT', path: '/folha-pagamento', permission: 'reports', adminOnly: true },
      ]
    }
  ];

  const getFilteredGroups = () => {
    if (isSuperAdmin() && location.pathname.startsWith('/admin-secret')) {
      return [{ title: 'Admin', items: superAdminItems }];
    }
    
    const rawPermissions = user?.permissions || [];
    const userPermissions = Array.isArray(rawPermissions) 
      ? rawPermissions 
      : Object.keys(rawPermissions).filter(k => rawPermissions[k]);
    
    const isAdminOrDiretoria = ['admin', 'super_admin', 'diretoria'].includes(user?.role);
    
    return menuGroups
      .filter(group => {
        // Se o grupo inteiro é adminOnly, só mostra para admin
        if (group.adminOnly) return isAdminOrDiretoria;
        return true;
      })
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          if (item.adminOnly) return isAdminOrDiretoria;
          if (isAdminOrDiretoria) return true;
          if (item.permission === 'settings' || item.permission === 'all') return true;
          return userPermissions.includes(item.permission);
        })
      }))
      .filter(group => group.items.length > 0);
  };

  const filteredGroups = getFilteredGroups();

  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className={cn(
        'h-16 flex items-center border-b border-zinc-800',
        collapsed ? 'justify-center px-2' : 'px-4'
      )}>
        <span className={cn(
          "font-bold tracking-[0.2em] text-cyan-400",
          collapsed ? "text-sm" : "text-base"
        )}>
          {collapsed ? 'T10' : 'TOMASELLI'}
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {filteredGroups.map((group, idx) => (
          <div key={group.title} className={cn(idx > 0 && 'mt-4')}>
            {!collapsed && (
              <p className={cn(
                "px-4 mb-2 text-[10px] font-mono font-medium uppercase tracking-[0.15em] flex items-center gap-1",
                group.adminOnly ? "text-amber-500" : "text-zinc-500"
              )}>
                {group.adminOnly && <FlaskConical className="w-3 h-3" />}
                {group.title}
              </p>
            )}
            <div className="space-y-0.5 px-2">
              {group.items.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                
                // Se for "Em breve", renderizar de forma diferente
                if (item.comingSoon) {
                  return (
                    <div
                      key={item.path}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm cursor-not-allowed opacity-50',
                        collapsed && 'justify-center',
                        'text-zinc-600'
                      )}
                      title="Em breve"
                    >
                      <item.icon className="w-[18px] h-[18px] flex-shrink-0 text-zinc-700" />
                      {!collapsed && (
                        <span className="flex items-center gap-2">
                          {item.label}
                          <span className="text-[9px] font-mono bg-amber-500/15 text-amber-400/80 px-1.5 py-0.5 rounded-sm">
                            Em breve
                          </span>
                        </span>
                      )}
                    </div>
                  );
                }
                
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={handleNavClick}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all cursor-pointer select-none active:opacity-70',
                      collapsed && 'justify-center',
                      isActive 
                        ? 'bg-cyan-500/15 text-white border-l-2 border-cyan-400' 
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                    )}
                  >
                    <item.icon className={cn(
                      'w-[18px] h-[18px] flex-shrink-0',
                      isActive ? 'text-cyan-400' : 'text-zinc-500'
                    )} />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-zinc-800 space-y-3">
        {/* Controls */}
        <div className={cn('flex items-center gap-2', collapsed && 'flex-col')}>
          <span className="px-2 py-0.5 text-[9px] font-mono font-medium text-cyan-400/80 bg-cyan-500/10 rounded-sm tracking-wider">
            BETA
          </span>
          {!collapsed && (
            <button
              onClick={() => setShowFeedback(true)}
              className="ml-auto text-[10px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
            >
              <MessageSquarePlus className="w-3 h-3" />
              Sugerir
            </button>
          )}
        </div>

        {/* User */}
        <div className={cn(
          'flex items-center gap-3 p-2 rounded-sm bg-cyan-500/5',
          collapsed && 'justify-center'
        )}>
          <div className="w-8 h-8 rounded-sm bg-cyan-500/20 flex items-center justify-center text-sm font-mono font-medium text-cyan-400">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{user?.name}</p>
              <p className="text-[10px] text-zinc-500 truncate">{user?.email}</p>
            </div>
          )}
        </div>

        {/* Logout */}
        <Button
          variant="ghost"
          data-testid="logout-button"
          onClick={handleLogout}
          className={cn(
            'w-full text-red-400/70 hover:text-red-400 hover:bg-red-500/10 rounded-sm',
            collapsed ? 'justify-center' : 'justify-start gap-2'
          )}
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span className="text-sm">Sair</span>}
        </Button>
      </div>

      {/* Collapse toggle - desktop only */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 rounded-full bg-gradient-to-b from-[#2a2020] to-[#1f1a1a] border border-stone-600/20 items-center justify-center text-stone-300/50 hover:text-white transition-colors"
      >
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
      </button>
    </>
  );

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-gradient-to-b from-[#2a2020] to-[#1f1a1a] border-b border-stone-600/10 flex items-center px-4 z-50">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 text-stone-200/60"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 ml-2">
          <span className="font-medium text-sm tracking-[0.2em] text-cyan-400">TOMASELLI</span>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/80 z-50"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={cn(
          'lg:hidden fixed left-0 top-0 bottom-0 w-72 bg-[#0A0A0A] border-r border-zinc-800 z-50 flex flex-col transition-transform duration-300',
          mobileOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
        )}
      >
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4 p-2 text-zinc-500"
        >
          <X className="w-5 h-5" />
        </button>
        <SidebarContent />
      </aside>

      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className={cn(
          'hidden lg:flex fixed left-0 top-0 bottom-0 flex-col bg-[#0A0A0A] border-r border-zinc-800 transition-all duration-300 z-40',
          collapsed ? 'w-16' : 'w-56'
        )}
      >
        <SidebarContent />
      </aside>
      
      {/* Feedback Dialog */}
      <Dialog open={showFeedback} onOpenChange={setShowFeedback}>
        <DialogContent className="max-w-sm bg-[#141416] border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Sugestão</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Sua opinião é importante
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Select value={feedbackType} onValueChange={setFeedbackType}>
              <SelectTrigger className="bg-amber-500/10 border-stone-600/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#352a2a] border-stone-600/20">
                <SelectItem value="sugestao">Sugestão</SelectItem>
                <SelectItem value="bug">Bug</SelectItem>
                <SelectItem value="melhoria">Melhoria</SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Descreva..."
              value={feedbackMessage}
              onChange={(e) => setFeedbackMessage(e.target.value)}
              rows={3}
              className="bg-amber-500/10 border-stone-600/20 text-white placeholder:text-stone-300/30 resize-none"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowFeedback(false)} className="text-stone-300">
              Cancelar
            </Button>
            <Button onClick={handleSendFeedback} disabled={sendingFeedback} className="bg-purple-600 hover:bg-amber-500">
              {sendingFeedback ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default Sidebar;
